import { api, ApiError, explain } from './api';
import type { Experiment, PublishState, Site } from './api';
import { alertBox, clear, field, h, statusBox } from './dom';
import { goalsPanel } from './goals-panel';
import { sectionsBuilder } from './mvt-form';
import { variationsBlock } from './variations-block';
import { navigate } from './router';

/** What every page gets: where to draw, and a signal that fires when the person navigates away. */
export interface Ctx {
  root: HTMLElement;
  signal: AbortSignal;
  email: string;
}

const heading = (text: string) => h('h1', {}, text);
const input = (label: string, attrs: Record<string, string> = {}) => {
  const el = h('input', { type: 'text', autocomplete: 'off', ...attrs });
  return { el, row: field(label, el) };
};

/** Run `fn` on a button press without double submits; show errors in `into`. */
function action(
  btn: HTMLButtonElement,
  into: HTMLElement,
  fn: () => Promise<void>,
  fallback?: string,
) {
  btn.addEventListener('click', async (e) => {
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    clear(into);
    try {
      await fn();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return navigate('/login', true);
      into.append(alertBox(explain(err, fallback)));
    } finally {
      btn.disabled = false;
    }
  });
}

// ---- sign in -----------------------------------------------------------------------------

export async function loginPage(ctx: Ctx): Promise<void> {
  const { root } = ctx;
  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  root.append(h('h1', {}, 'Sign in'));
  const msg = h('div', {});

  if (token) {
    history.replaceState(null, '', '/login'); // never leave a sign-in token in the address bar
    root.append(msg);
    try {
      await api('POST', '/auth/verify', { token });
      return navigate('/sites', true);
    } catch (e) {
      msg.append(alertBox(explain(e)));
    }
  }

  const email = input('Email address', { type: 'email', autocomplete: 'email', required: 'true' });
  const send = h('button', { type: 'submit' }, 'Send sign-in link');
  // novalidate: our own message explains a mistyped address better than the browser's tooltip
  const form = h(
    'form',
    { novalidate: true },
    h(
      'p',
      {},
      'Enter your email and we will send you a link that signs you in. No password needed.',
    ),
    email.row,
    send,
  );
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    clear(msg);
    send.disabled = true;
    try {
      await api('POST', '/auth/login', { email: email.el.value });
      msg.append(
        statusBox(
          'Check your email: we sent a sign-in link. It works once and expires in 15 minutes.',
        ),
      );
    } catch (err) {
      msg.append(alertBox(explain(err)));
    } finally {
      send.disabled = false;
    }
  });
  if (!token) root.append(msg);
  root.append(form);
  if (token) root.append(msg);
}

// ---- sites -------------------------------------------------------------------------------

export async function sitesPage(ctx: Ctx): Promise<void> {
  const { root } = ctx;
  root.append(heading('Your sites'));
  const list = h('ul', { class: 'cards' });
  const empty = h(
    'p',
    { class: 'muted' },
    'You have not added a site yet. Add the website you want to test.',
  );
  const draw = async () => {
    const { sites } = await api<{ sites: Site[] }>('GET', '/sites');
    clear(list);
    empty.hidden = sites.length > 0;
    for (const s of sites)
      list.append(
        h(
          'li',
          {},
          h('a', { href: `/sites/${s.id}` }, s.name),
          ' ',
          h('span', { class: 'muted' }, s.domain),
          ' ',
          h(
            'span',
            { class: s.verified ? 'badge ok' : 'badge' },
            s.verified ? 'Verified' : 'Not verified yet',
          ),
        ),
      );
  };
  root.append(empty, list);

  const name = input('Site name', { maxlength: '80' });
  const domain = input('Domain', { placeholder: 'shop.example.com' });
  const add = h('button', { type: 'submit' }, 'Add site');
  const out = h('div', {});
  const form = h('form', { class: 'row' }, name.row, domain.row, add);
  form.addEventListener('submit', (e) => e.preventDefault());
  action(add, out, async () => {
    await api('POST', '/sites', { name: name.el.value, domain: domain.el.value });
    name.el.value = '';
    domain.el.value = '';
    await draw();
  });
  root.append(h('h2', {}, 'Add a site'), form, out);
  await draw();
}

// ---- one site ----------------------------------------------------------------------------

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'never');

export function notFoundPage(ctx: Ctx, what = 'Page'): void {
  ctx.root.append(
    h('h1', {}, `${what} not found`),
    h('p', {}, h('a', { href: '/sites' }, 'Back to your sites')),
  );
}

/** Published version, unpublished-changes flag and the Publish button, for one site. */
function publishPanel(siteId: string, ctx: Ctx, refreshOthers?: () => void) {
  const box = h('section', { 'aria-label': 'Publishing' });
  const out = h('div', {});
  const publish = h('button', { type: 'button' }, 'Publish changes');
  const line = h('p', {});
  box.append(h('h2', {}, 'Publishing'), line, publish, out);
  const draw = async () => {
    const s = await api<PublishState>('GET', `/sites/${siteId}/publish`);
    clear(line);
    line.append(s.version ? `Published version ${s.version}` : 'Not published yet');
    if (s.unpublishedChanges)
      line.append(' ', h('span', { class: 'badge warn' }, 'Unpublished changes'));
    publish.hidden = !s.unpublishedChanges;
    if (s.blocked?.[0]) out.append(alertBox(`Cannot publish yet: ${s.blocked[0].message}.`));
  };
  action(publish, out, async () => {
    await api('POST', `/sites/${siteId}/publish`, {});
    await draw();
    refreshOthers?.();
  });
  void draw().catch(() => {});
  return { el: box, refresh: draw };
}

export async function sitePage(ctx: Ctx, id: string): Promise<void> {
  const { root } = ctx;
  let site: Site;
  try {
    site = (await api<{ site: Site }>('GET', `/sites/${id}`)).site;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return notFoundPage(ctx, 'Site');
    throw e;
  }
  root.append(
    h('p', {}, h('a', { href: '/sites' }, 'All sites')),
    heading(site.name),
    h('p', { class: 'muted' }, site.domain),
  );

  // verification
  const verifyLine = h('p', {});
  const drawVerify = (verified: boolean, last: string | null) => {
    clear(verifyLine);
    verifyLine.append(
      verified
        ? h('span', { class: 'badge ok' }, 'Verified')
        : h('span', { class: 'badge' }, 'Not verified yet'),
      ' ',
      h(
        'span',
        { class: 'muted' },
        verified
          ? `Last seen ${when(last)}`
          : 'Paste the snippet below into your site, then open any page of it.',
      ),
    );
  };
  drawVerify(site.verified, site.lastPingAt);
  root.append(verifyLine);
  if (!site.verified) {
    const timer = setInterval(async () => {
      try {
        const s = (await api<{ site: Site }>('GET', `/sites/${id}`)).site;
        drawVerify(s.verified, s.lastPingAt);
        if (s.verified) clearInterval(timer);
      } catch {
        /* keep trying */
      }
    }, 2000);
    ctx.signal.addEventListener('abort', () => clearInterval(timer));
  }

  // install snippet
  const snip = await api<{ snippet: string; instructions: string[] }>(
    'GET',
    `/sites/${id}/snippet`,
  );
  const area = h('textarea', {
    readonly: true,
    rows: '5',
    'aria-label': 'Install snippet',
    spellcheck: 'false',
  });
  area.value = snip.snippet;
  const copied = h('span', { class: 'muted', role: 'status' });
  const copy = h('button', { type: 'button' }, 'Copy snippet');
  copy.addEventListener('click', async () => {
    area.select();
    try {
      await navigator.clipboard.writeText(snip.snippet);
      copied.textContent = 'Copied';
    } catch {
      copied.textContent = 'Press Ctrl+C to copy';
    }
  });
  root.append(
    h('h2', {}, 'Install'),
    area,
    h('p', {}, copy, ' ', copied),
    h('ol', {}, ...snip.instructions.map((t) => h('li', {}, t))),
  );

  // settings
  const consent = h('input', { type: 'checkbox' });
  consent.checked = site.consentRequired;
  const flicker = h('input', {
    type: 'number',
    min: '0',
    max: '5000',
    step: '100',
    placeholder: 'default',
  });
  flicker.value = site.antiFlickerMs === null ? '' : String(site.antiFlickerMs);
  const save = h('button', { type: 'button' }, 'Save settings');
  const saved = h('div', {});
  const pub = publishPanel(id, ctx); // created first: saving settings refreshes it
  root.append(
    h('h2', {}, 'Settings'),
    h('label', { class: 'check' }, consent, h('span', {}, 'Ask visitors for consent first')),
    field('Hide the page while changes load (ms)', flicker),
    save,
    saved,
  );
  action(save, saved, async () => {
    await api('PATCH', `/sites/${id}`, {
      consentRequired: consent.checked,
      antiFlickerMs: flicker.value === '' ? null : Number(flicker.value),
    });
    saved.append(statusBox('Settings saved'));
    await pub.refresh();
  });

  root.append(pub.el);

  // experiments
  const list = h('ul', { class: 'cards' });
  const none = h('p', { class: 'muted' }, 'No experiments yet.');
  const drawExperiments = async () => {
    const { experiments } = await api<{ experiments: Experiment[] }>(
      'GET',
      `/sites/${id}/experiments`,
    );
    clear(list);
    none.hidden = experiments.length > 0;
    for (const e of experiments)
      list.append(
        h(
          'li',
          {},
          h('a', { href: `/experiments/${e.id}` }, e.name),
          ' ',
          h('span', { class: `badge ${e.status}` }, e.status),
        ),
      );
  };
  const ename = input('Experiment name', { maxlength: '120' });
  const create = h('button', { type: 'submit' }, 'New experiment');
  const eout = h('div', {});
  const multi = h('input', { type: 'checkbox' }) as HTMLInputElement;
  const builder = sectionsBuilder();
  builder.el.hidden = true;
  multi.addEventListener('change', () => (builder.el.hidden = !multi.checked));
  const eform = h(
    'form',
    {},
    h('div', { class: 'row' }, ename.row, create),
    h(
      'label',
      { class: 'check' },
      multi,
      'Multivariate test: try several parts of the page at once (for example a headline and a button)',
    ),
    builder.el,
  );
  eform.addEventListener('submit', (e) => e.preventDefault());
  action(create, eout, async () => {
    if (!multi.checked) {
      await api('POST', `/sites/${id}/experiments`, { name: ename.el.value });
    } else {
      const p = builder.read();
      if (p.problem) {
        eout.append(alertBox(p.problem));
        return;
      }
      await api('POST', `/sites/${id}/experiments`, {
        name: ename.el.value,
        multivariate: { sections: p.sections },
      });
      builder.reset();
      multi.checked = false;
      builder.el.hidden = true;
    }
    ename.el.value = '';
    await drawExperiments();
  });
  root.append(h('h2', {}, 'Experiments'), none, list, eform, eout);
  await drawExperiments();
}

// ---- one experiment ----------------------------------------------------------------------

const STATUS_LABEL = {
  draft: 'Draft',
  running: 'Running',
  paused: 'Paused',
  stopped: 'Stopped',
} as const;

export async function experimentPage(ctx: Ctx, id: string): Promise<void> {
  const { root } = ctx;
  let exp: Experiment;
  try {
    exp = (await api<{ experiment: Experiment }>('GET', `/experiments/${id}`)).experiment;
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return notFoundPage(ctx, 'Experiment');
    throw e;
  }
  root.append(
    h('p', {}, h('a', { href: `/sites/${exp.siteId}` }, 'Back to site')),
    heading(exp.name),
    h('p', {}, h('a', { href: `/experiments/${exp.id}/results` }, 'View results')),
  );

  const status = h('p', {});
  const controls = h('p', { class: 'row' });
  const out = h('div', {});
  const confirm = h('div', {});
  root.append(status, controls, confirm, out);
  const pub = publishPanel(exp.siteId, ctx);

  const change = async (next: string) => {
    const r = await api<{ experiment: Experiment; publishError?: unknown }>(
      'POST',
      `/experiments/${exp.id}/status`,
      { status: next },
    );
    Object.assign(exp, r.experiment); // in place: the goals panel holds this same object (and its version)
    drawStatus();
    await pub.refresh();
    if (r.publishError)
      out.append(
        alertBox('The status changed, but publishing failed. Open the site page to see why.'),
      );
  };
  const btn = (label: string, next: string, danger = false) => {
    const b = h('button', { type: 'button', class: danger ? 'danger' : undefined }, label);
    action(b, out, () => change(next));
    return b;
  };
  const drawStatus = () => {
    clear(status);
    clear(controls);
    clear(confirm);
    status.append(`Status: ${STATUS_LABEL[exp.status]}`);
    if (exp.status === 'draft') controls.append(btn('Start experiment', 'running'));
    if (exp.status === 'running') controls.append(btn('Pause', 'paused'));
    if (exp.status === 'paused') controls.append(btn('Resume', 'running'));
    if (exp.status === 'running' || exp.status === 'paused') {
      const stop = h('button', { type: 'button', class: 'danger' }, 'Stop');
      stop.addEventListener('click', () => {
        clear(confirm);
        const yes = btn('Yes, stop it', 'stopped', true);
        const no = h('button', { type: 'button' }, 'Cancel');
        no.addEventListener('click', () => clear(confirm));
        confirm.append(
          h(
            'p',
            {},
            'Stopping is permanent: visitors go back to the original page and this experiment cannot be started again.',
          ),
          yes,
          ' ',
          no,
        );
      });
      controls.append(stop);
    }
  };
  drawStatus();

  // goals: what counts as success (no goal, no results)
  root.append(
    goalsPanel(exp, ctx.signal, async () => {
      // a running experiment serves the published copy, so a changed goal must be published to count
      if (exp.status === 'running' || exp.status === 'paused')
        await api('POST', `/sites/${exp.siteId}/publish`, {});
      await pub.refresh();
    }),
  );

  variationsBlock(exp, ctx, pub.el, root, action);
}
